import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Notify } from '@shared/ui-native/Notify';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';
import { Button } from '@shared/ui-react/Button';
import { Events } from '@shared/Events';

interface DocumentMetadata {
    id: number;
    uuid: string;
    name: string;
    version: number;
}

interface TxtViewerProps {
    txtUrl: string;
    height?: string;
    canEdit?: boolean;
    documentUuid: string;
    onClose?: () => void;
    onNewVersionCreated?: (newDocument: any) => void;
}

export const TxtViewer = ({ txtUrl, height, canEdit = false, documentUuid, onClose, onNewVersionCreated }: TxtViewerProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [content, setContent] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editableContent, setEditableContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [documentMetadata, setDocumentMetadata] = useState<DocumentMetadata | null>(null);

    const scrollPositionRef = useRef(0);
    const viewRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const displayContent = isEditing ? editableContent : content;
    const hasChanges = editableContent !== content;
    const characterCount = displayContent.length;
    const lineCount = displayContent.split('\n').length;

    const loadDocumentData = async () => {
        setIsLoading(true);
        setIsError(false);
        setErrorMessage('');

        try {
            const [metadataResponse, contentResponse] = await Promise.all([axios.get(`/kuura/api/1/file/details/${documentUuid}`), axios.get(txtUrl, { responseType: 'text' })]);

            setDocumentMetadata(metadataResponse.data.model);
            setContent(contentResponse.data);
            setEditableContent(contentResponse.data);
            setIsLoading(false);
        } catch (error) {
            setIsError(true);
            setErrorMessage(__('js_txtviewer.error_load_failed'));
            setIsLoading(false);
            Notify.error(__('js_txtviewer.error_load_failed'));
        }
    };

    useEffect(() => {
        loadDocumentData();
    }, [documentUuid, txtUrl]);

    useEffect(() => {
        const element = isEditing ? textareaRef.current : viewRef.current;
        if (element) element.scrollTop = scrollPositionRef.current;
    }, [isEditing]);

    const toggleEdit = () => {
        const element = isEditing ? textareaRef.current : viewRef.current;
        if (element) scrollPositionRef.current = element.scrollTop;

        setIsEditing(!isEditing);
        if (!isEditing) setEditableContent(content);
    };

    const onContentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditableContent(event.target.value);
    };

    const saveChanges = async () => {
        if (!documentMetadata?.id) {
            Notify.error(__('js_txtviewer.upload_failed'));
            return;
        }

        if (textareaRef.current) {
            scrollPositionRef.current = textareaRef.current.scrollTop;
        }

        setIsSaving(true);

        try {
            const formData = new FormData();
            formData.append('name', documentMetadata.name);
            formData.append('description', '');
            formData.append('file', new File([editableContent], documentMetadata.name, { type: 'text/plain' }));

            const response = await axios.post(`/kuura/api/1/file/${documentMetadata.id}/edit.json`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data.status === true) {
                setContent(editableContent);
                setIsEditing(false);
                setIsSaving(false);

                Notify.success(__('js_txtviewer.new_version_created'));
                Events.trigger('tree-fetch');
                Events.trigger('explorer_items_changed');

                if (onNewVersionCreated) {
                    onNewVersionCreated({
                        id: response.data.fileId,
                        uuid: response.data.fileUuid,
                        name: response.data.name,
                        newBinarydata: response.data.newBinarydata,
                    });
                } else {
                    Events.trigger('file-updated', {
                        oldUuid: documentUuid,
                        newUuid: response.data.fileUuid,
                        newFileId: response.data.fileId,
                        newFileName: response.data.name,
                    });
                }
            } else {
                throw new Error('Server returned error status');
            }
        } catch (error) {
            setIsSaving(false);
            console.error('Upload failed:', error);

            const errorMsg = (error as any).response?.data?.errorMessages?.[0] || __('js_txtviewer.upload_failed');
            Notify.error(errorMsg);
        }
    };

    const cancelEdit = () => {
        if (textareaRef.current) {
            scrollPositionRef.current = textareaRef.current.scrollTop;
        }

        setEditableContent(content);
        setIsEditing(false);
    };

    if (isLoading) {
        return <Skeleton width='100%' height={height || '100%'} />;
    }

    if (isError) {
        return (
            <div
                style={{
                    width: '100%',
                    height: height || '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '20px',
                }}
            >
                <Message severity='error' text={errorMessage} style={{ width: '100%', marginBottom: '20px' }} />
                <Button secondary onClick={loadDocumentData} icon='iki iki-refresh'>
                    {__('js_txtviewer.retry')}
                </Button>
            </div>
        );
    }

    return (
        <div
            style={{
                width: '100%',
                height: height || '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {canEdit && (
                <div style={{ marginBottom: 10, flexShrink: 0 }}>
                    <nav
                        className='floating-window-toolbar'
                        style={{
                            justifyContent: 'space-between',
                            alignItems: 'stretch',
                            padding: '8px 2px',
                        }}
                    >
                        <div className='flex-row gap-half'>
                            {onClose && <Button secondary subtle icon='iki iki-arrow-left' onClick={onClose} style={{ paddingLeft: 20, paddingRight: 20 }} />}
                            <Button subtle icon='iki iki-refresh' tooltip={__('js_txtviewer.reload_content')} onClick={loadDocumentData} />
                        </div>
                        <div className='flex-row gap-half'>
                            {isEditing ? (
                                <>
                                    <Button secondary onClick={cancelEdit} disabled={isSaving}>
                                        {__('general.cancel')}
                                    </Button>
                                    <Button primary onClick={saveChanges} disabled={!hasChanges || isSaving} loading={isSaving}>
                                        {isSaving ? __('js_txtviewer.creating_version') : __('general.save')}
                                    </Button>
                                </>
                            ) : (
                                <Button primary onClick={toggleEdit} icon='iki iki-edit'>
                                    {__('general.edit')}
                                </Button>
                            )}
                        </div>
                    </nav>
                </div>
            )}

            <div
                style={{
                    flex: 1,
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    position: 'relative',
                }}
            >
                {isEditing ? (
                    <textarea
                        ref={textareaRef}
                        value={editableContent}
                        onChange={onContentChange}
                        disabled={isSaving}
                        style={{
                            width: '100%',
                            height: '100%',
                            padding: '16px',
                            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            backgroundColor: isSaving ? '#f0f0f0' : '#f8f9fa',
                            boxSizing: 'border-box',
                            opacity: isSaving ? 0.7 : 1,
                        }}
                        placeholder={__('js_txtviewer.placeholder')}
                    />
                ) : (
                    <div
                        ref={viewRef}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            padding: '16px',
                            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflow: 'auto',
                            backgroundColor: '#ffffff',
                            boxSizing: 'border-box',
                        }}
                    >
                        {content || <em style={{ color: '#666' }}>{__('js_txtviewer.no_content')}</em>}
                    </div>
                )}
            </div>

            <div
                style={{
                    padding: '8px 16px',
                    backgroundColor: '#f8f9fa',
                    borderTop: '1px solid #ddd',
                    fontSize: '12px',
                    color: '#666',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                }}
            >
                <span>
                    {characterCount} {__('js_txtviewer.characters')}, {lineCount} {__('js_txtviewer.lines')}
                </span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {hasChanges && <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>• {__('js_txtviewer.unsaved_changes')}</span>}
                    {isSaving && <span style={{ color: '#3498db', fontWeight: 'bold' }}>• {__('js_txtviewer.uploading')}</span>}
                </div>
            </div>
        </div>
    );
};
